import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Clock,
  Send,
  ChevronLeft,
  ChevronRight,
  Phone,
  MapPin,
  Gauge,
  User,
  FileText,
  Camera,
  Pencil,
  Check,
  X,
} from 'lucide-react';
import { Bounce, toast } from 'react-toastify';
import useGetFineByTicketNumber from 'hooks/fines/useGetFineByTicketNumber';
import useOfficerNotifyFine from 'hooks/fines/useOfficerNotifyFine';
import { useFines } from 'context/FinesContext';
import FineDetailsSkeleton from 'components/common/skeletons/FineDetailsSkeleton';
import NoDataHandling from 'components/shared/NoData/NoDataHandling';
import ZoomableImage from 'components/common/ZoomableImage';
import useUpdateOwnerPhone from 'hooks/ntsa/Useupdateownerphone';

// ─── SMS status helpers ───────────────────────────────────────────────────────

type SmsStatus = 'S' | 'P' | 'F' | null;

const getSmsConfig = (status: SmsStatus) => {
  switch (status) {
    case 'S':
      return {
        label: 'Sent',
        icon: <CheckCircle size={14} className="text-emerald-600" />,
        badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      };
    case 'P':
      return {
        label: 'Pending',
        icon: <Clock size={14} className="text-amber-600" />,
        badge: 'bg-amber-50 text-amber-700 border-amber-200',
      };
    case 'F':
      return {
        label: 'Failed',
        icon: <AlertTriangle size={14} className="text-red-600" />,
        badge: 'bg-red-50 text-red-700 border-red-200',
      };
    default:
      return {
        label: 'No Status',
        icon: <AlertTriangle size={14} className="text-gray-400" />,
        badge: 'bg-gray-50 text-gray-500 border-gray-200',
      };
  }
};

// ─── Section ──────────────────────────────────────────────────────────────────

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, title, children, action }) => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className="text-gray-500">{icon}</span>
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── Info row ─────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-800">{value ?? '—'}</p>
  </div>
);

// ─── Phone edit field ─────────────────────────────────────────────────────────

const PhoneEditField: React.FC<{
  initialPhone: string | null;
  ownerId: number;
  onSaved: (phone: string) => void;
}> = ({ initialPhone, ownerId, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState(initialPhone ?? '');

  const { mutateAsync: updatePhone, isPending: isSaving } =
    useUpdateOwnerPhone();

  const handleSave = async () => {
    if (!phone.trim()) return;
    await updatePhone({ ownerId, phoneNumber: phone.trim() })
      .then(() => {
        toast.success('Phone number updated successfully', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'light',
          transition: Bounce,
        });
        onSaved(phone.trim());
        setEditing(false);
      })
      .catch(() =>
        toast.error('Failed to update phone number', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'light',
          transition: Bounce,
        }),
      );
  };

  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">Phone Number</p>
      {editing ? (
        <div className="flex items-center gap-2 mt-1">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="07XXXXXXXX"
            className="flex-1 px-3 py-1.5 text-sm border border-blue-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 transition"
            autoFocus
          />
          <button
            onClick={handleSave}
            disabled={isSaving || !phone.trim()}
            className="p-1.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 transition"
          >
            {isSaving ? (
              <svg
                className="animate-spin h-3.5 w-3.5"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <Check size={14} />
            )}
          </button>
          <button
            onClick={() => {
              setPhone(initialPhone ?? '');
              setEditing(false);
            }}
            disabled={isSaving}
            className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition disabled:opacity-50"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-0.5">
          {phone ? (
            <p className="text-sm font-medium text-gray-800">{phone}</p>
          ) : (
            <p className="text-sm text-red-500 font-medium">
              ⚠️ No phone number on record
            </p>
          )}
          <button
            onClick={() => setEditing(true)}
            className="p-1 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
            title={phone ? 'Edit phone number' : 'Add phone number'}
          >
            <Pencil size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── SMS Confirm modal ────────────────────────────────────────────────────────

const SmsModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
  isSending: boolean;
  phone: string | null;
  isResend: boolean;
}> = ({ onConfirm, onCancel, isSending, phone, isResend }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div
      className="absolute inset-0 bg-black/40"
      onClick={!isSending ? onCancel : undefined}
    />
    <div className="relative bg-white rounded-2xl shadow-xl p-6 w-80 flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-1">
          {isResend ? 'Resend SMS Notification?' : 'Send SMS Notification?'}
        </h2>
        <p className="text-sm text-gray-500">
          This will send an SMS to{' '}
          <span className="font-medium text-gray-800">
            {phone ?? 'the vehicle owner'}
          </span>{' '}
          about this fine.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={isSending}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isSending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition"
        >
          {isSending ? (
            <>
              <svg
                className="animate-spin h-3 w-3"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Sending…
            </>
          ) : (
            <>
              <Send size={13} />
              {isResend ? 'Resend SMS' : 'Send SMS'}
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const NTSAFineDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: fine, isLoading } = useGetFineByTicketNumber(id ?? '');
  const { mutateAsync: notifyFineMutation, isPending: isSendingSms } =
    useOfficerNotifyFine();
  const { fetchNextFine, fetchPreviousFine } = useFines();

  const [showSmsModal, setShowSmsModal] = useState(false);
  // Track phone locally so PhoneEditField updates reflect immediately
  const [localPhone, setLocalPhone] = useState<string | null>(null);

  // Initialise localPhone once fine loads
  React.useEffect(() => {
    if (fine?.ownerDetails?.phoneNumber) {
      setLocalPhone(fine.ownerDetails.phoneNumber);
    }
  }, [fine]);

  const phoneNumber = localPhone ?? fine?.ownerDetails?.phoneNumber ?? null;
  const missingPhone = !phoneNumber;
  const smsCfg = getSmsConfig((fine?.smsStatus as SmsStatus) ?? null);
  const smsSent = fine?.smsStatus === 'S';
  const exceeded = fine ? fine.vehicleSpeed - fine.speedLimit : 0;

  const handleNext = async () => {
    if (!fine) return;
    const next = await fetchNextFine(String(fine.ticketNumber));
    if (next) navigate(`/fines/single/${next.ticketNumber}`);
  };

  const handlePrev = async () => {
    if (!fine) return;
    const prev = await fetchPreviousFine(String(fine.ticketNumber));
    if (prev) navigate(`/fines/single/${prev.ticketNumber}`);
  };

  const handleSendSms = async () => {
    await notifyFineMutation({ fineId: (fine?.id ?? '').toString() })
      .then(() => {
        toast.success('SMS notification sent successfully', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'light',
          transition: Bounce,
        });
        setShowSmsModal(false);
      })
      .catch(() =>
        toast.error('Error sending SMS notification', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'light',
          transition: Bounce,
        }),
      );
  };

  if (isLoading) return <FineDetailsSkeleton />;
  if (!fine) return <NoDataHandling content="No fine found for this ticket." />;

  return (
    <div className="relative">
      <div className="absolute inset-0 z-10 grid grid-cols-4 gap-8 opacity-[0.1] pointer-events-none">
        {Array.from({ length: 50 }).map((_, idx) => (
          <div key={idx} className="flex items-center justify-center">
            <img
              src="/ntsa-logo.svg"
              alt=""
              className="w-16 h-16 object-contain"
            />
          </div>
        ))}
      </div>
      <div className="relative z-0 bg-gray-50">
        {showSmsModal && (
          <SmsModal
            onConfirm={handleSendSms}
            onCancel={() => setShowSmsModal(false)}
            isSending={isSendingSms}
            phone={phoneNumber}
            isResend={smsSent}
          />
        )}

        {/* ── Header ── */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className=" flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <div className="h-4 w-px bg-gray-200" />
              <div>
                <h1 className="text-base font-semibold text-gray-900">
                  Ticket {String(fine.ticketNumber).slice(-6).toUpperCase()}
                </h1>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${smsCfg.badge}`}
                  >
                    {smsCfg.icon}
                    SMS {smsCfg.label}
                  </span>
                  {missingPhone && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200">
                      <Phone size={11} />
                      No phone
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Prev / Next */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="max-w-8xl mx-auto px-6 py-6 space-y-4">
          {/* Ticket summary */}
          <Section icon={<FileText size={16} />} title="Ticket Summary">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <InfoRow
                label="Vehicle Registration"
                value={fine.vehicleRegistrationNumber}
              />
              <InfoRow
                label="Location"
                value={
                  <span className="flex items-center gap-1">
                    <MapPin size={12} className="text-gray-400" />
                    {fine.location}
                  </span>
                }
              />
              <InfoRow
                label="Date Captured"
                value={moment(fine.dateCaptured).format('D MMM YYYY, h:mm a')}
              />
            </div>
          </Section>

          {/* Speed details */}
          <Section icon={<Gauge size={16} />} title="Speed Details">
            <div className="grid grid-cols-3 gap-5">
              <InfoRow label="Speed Limit" value={`${fine.speedLimit} km/h`} />
              <InfoRow
                label="Vehicle Speed"
                value={`${fine.vehicleSpeed} km/h`}
              />
              <InfoRow
                label="Exceeded By"
                value={
                  <span
                    className={
                      exceeded > 0 ? 'text-red-600' : 'text-emerald-600'
                    }
                  >
                    {exceeded > 0 ? `+${exceeded}` : exceeded} km/h
                  </span>
                }
              />
            </div>
          </Section>

          {/* Infractions */}
          {fine.finesOffensesDetails &&
            fine.finesOffensesDetails.length > 0 && (
              <Section icon={<FileText size={16} />} title="Infraction">
                <div className="space-y-3">
                  {fine.finesOffensesDetails.map((offence, i) => (
                    <div
                      key={i}
                      className="bg-gray-50 rounded-xl p-4 border border-gray-100"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoRow
                          label="Date & Time"
                          value={moment(fine.dateCaptured).format(
                            'D MMM YYYY, h:mm a',
                          )}
                        />
                        <InfoRow label="Type" value={offence.offenceName} />
                        {offence.sectionAct && (
                          <InfoRow label="Section" value={offence.sectionAct} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

          {/* Owner details */}
          <Section icon={<User size={16} />} title="Owner Details">
            {fine.ownerDetails ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-5">
                  <InfoRow
                    label="Name"
                    value={`${fine.ownerDetails.name} ${fine.ownerDetails.lastName}`}
                  />
                  <PhoneEditField
                    initialPhone={phoneNumber}
                    ownerId={52}
                    onSaved={(p) => setLocalPhone(p)}
                  />
                </div>

                {/* SMS sent confirmation */}
                {smsSent && phoneNumber && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                    <CheckCircle
                      size={14}
                      className="text-emerald-600 shrink-0"
                    />
                    <p className="text-xs text-emerald-700">
                      SMS sent to{' '}
                      <span className="font-semibold">{phoneNumber}</span>
                      {/* {fine.smsSentDate
                      ? ` on ${moment(fine.smsSentDate).format('D MMM YYYY, h:mm a')}`
                      : ''} */}
                    </p>
                  </div>
                )}

                {/* Missing phone warning */}
                {missingPhone && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <AlertTriangle
                      size={14}
                      className="text-amber-600 shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-amber-700">
                      No phone number on record. Add one above to enable SMS
                      notification.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Owner details not found.</p>
            )}
          </Section>

          {/* Evidence */}
          {fine.imageUrl && (
            <Section icon={<Camera size={16} />} title="Evidence">
              <ZoomableImage
                image={fine.imageUrl}
                alt="Fine evidence"
                maxZoom={4}
              />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
};

export default NTSAFineDetails;
